/**
 * Face-visible gate for the representative selfie — HONEST by construction.
 *
 * The bundle ships no face detector (the droom's INTAKE-09 §3.2 discipline: never a fabricated
 * "looks good" cue). What a browser MAY offer is the Shape Detection API's `FaceDetector`
 * (Chrome/Android behind flags; absent on most handsets). So:
 *   • available + 0 faces  → the shot is refused with a named reason (retake);
 *   • available + ≥ 1 face → the gate passes (still a UX gate — SV_PRESENTER_FACE is server-side);
 *   • unavailable          → NOT_EVALUATED: the UI says the face check will run on the server,
 *                            and telemetry records that the edge gate could not run.
 * Nothing here is evidence; the server's `Verified engine` row is.
 */

export interface FaceGateResult {
  readonly evaluated: boolean;
  readonly faces: number | null;
  readonly ok: boolean;
}

type FaceDetectorLike = new (opts?: { fastMode?: boolean; maxDetectedFaces?: number }) => {
  detect: (src: ImageBitmapSource) => Promise<readonly unknown[]>;
};

export function faceDetectorAvailable(): boolean {
  return typeof window !== 'undefined' && typeof (window as { FaceDetector?: unknown }).FaceDetector === 'function';
}

export async function assessFaceVisible(blob: Blob): Promise<FaceGateResult> {
  if (!faceDetectorAvailable() || typeof createImageBitmap !== 'function') {
    return { evaluated: false, faces: null, ok: false };
  }
  try {
    const Detector = (window as unknown as { FaceDetector: FaceDetectorLike }).FaceDetector;
    const detector = new Detector({ fastMode: true, maxDetectedFaces: 3 });
    const bmp = await createImageBitmap(blob);
    try {
      const faces = await detector.detect(bmp);
      return { evaluated: true, faces: faces.length, ok: faces.length >= 1 };
    } finally {
      bmp.close();
    }
  } catch {
    return { evaluated: false, faces: null, ok: false };
  }
}
